import { Form, SchemaBuilder, useHookForm } from '@aspendental/shared-forms-web';
import { Alert, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import Cookies from 'universal-cookie';
import Address, { generateAddressFormSchema } from './Address';
import { getExperimentData, IPatientInformationProps, parseAndSaveMarketingData } from './PatientInformation.helpers';
import PersonalInfo, { generatePersonalInfoFormSchema } from './PersonalInfo';
import { useFormStoreSync } from '@src/hooks/useFormStoreSync';
import { getLogger } from '@src/lib/logging/logger';
import { createAnswerObject } from '@src/pages/utils/create-answer-object';
import { useAppStore, usePatientInformationStore, useScheduleQuestionsStore, useStepManagerStore } from '@src/store';
import makeRequestHeaders from '@src/utils/make-request-headers';
import { unacceptableInputs } from '@src/utils/offensive-words';
declare global {
  interface Window {
    ga: any;
    LogRocket: any;
  }
}
const errorMapping: Record<string, {
  field: string;
  message: string;
}> = {
  ec2: {
    field: 'phone',
    message: 'phoneRequired'
  },
  ec6: {
    field: 'firstName',
    message: 'validFirstNameRequired'
  },
  ec7: {
    field: 'lastName',
    message: 'validLastNameRequired'
  }
};
export default function PatientInformation({
  smartyKey,
  setModalType
}: Readonly<IPatientInformationProps>) {
  const router = useRouter();
  const {
    locale
  } = router;
  const logger = getLogger('app');
  const languagePreference = locale === 'es-US' ? 'Spanish' : 'English';
  const {
    t
  } = useTranslation('index');
  const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
  const {
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    address,
    zipCode,
    city,
    state,
    leadId
  } = usePatientInformationStore(store => store);
  const [GATrackingId, setGATrackingId] = useState('');
  const [GAClientId, setGAClientId] = useState('');
  const [gclid, setGclid] = useState('');
  const {
    isStepSubmitting,
    setIsStepSubmitting,
    incrementStep,
    setStepOnePtc
  } = useStepManagerStore(store => store);
  const generatedPersonalInfoFormSchema = generatePersonalInfoFormSchema({
    firstNameRequired: t('firstNameRequired'),
    max30Chars: t('maximumChars'),
    lastNameRequired: t('lastNameRequired'),
    validEmail: t('emailRequired'),
    dateOfBirthRequired: t('dateOfBirthRequired'),
    minYear: t('minimumYear'),
    patientMinAge: t('minimumPatientAge'),
    phoneRequired: t('phoneRequired')
  });
  const generatedAddressFormSchema = generateAddressFormSchema({
    addressRequired: t('required'),
    cityRequired: t('cityRequired'),
    zipCodeRequired: t('zipCodeRequired'),
    max30Chars: t('maximumChars'),
    stateRequired: t('stateRequired')
  });
  const schema = SchemaBuilder([...generatedPersonalInfoFormSchema, ...generatedAddressFormSchema]);
  const {
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setDateOfBirth,
    setAddress,
    setZipCode,
    setCity,
    setState,
    setLeadId,
    setSalesforceGuid,
    setSalesforceAccountId
  } = usePatientInformationStore(store => store);
  const {
    leadFormId,
    enableHydraCalls,
    sessionId,
    transformMarketingSourceEnabled,
    referrer
  } = useAppStore(store => store);
  const {
    questionData,
    questionsAnswers,
    setSchedulePopupError
  } = useScheduleQuestionsStore(store => store);
  const defaultValues = useMemo(() => ({
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    address,
    zipCode,
    city,
    state
  }), [firstName, lastName, email, phone, dateOfBirth, address, zipCode, city, state]);
  const form = useHookForm({
    schema,
    defaultValues
  });
  const updateStoreValues = useCallback((values: FieldValues) => {
    setFirstName(values.firstName?.trim() ?? '');
    setLastName(values.lastName?.trim() ?? '');
    setEmail(values.email?.trim() ?? '');
    setPhone(values.phone ?? '');
    setDateOfBirth(values.dateOfBirth ?? '');
    setAddress(values.address?.trim() ?? '');
    setZipCode(values.zipCode ?? '');
    setCity(values.city?.trim() ?? '');
    setState(values.state ?? '');
  }, [setFirstName, setLastName, setEmail, setPhone, setDateOfBirth, setAddress, setZipCode, setCity, setState]);
  useFormStoreSync({
    form,
    updateStore: updateStoreValues
  });
  useEffect(() => {
    setGclid(new URLSearchParams(window.location.search).get('gclid') || localStorage.getItem('cc-gclId') || '');
    if (typeof window !== 'undefined' && typeof window.ga !== 'undefined') {
      window.ga(function () {
        const tracker = window.ga.getAll()[0];
        if (tracker) {
          setGATrackingId(tracker.get('trackingId'));
          setGAClientId(tracker.get('clientId'));
        } else {
          console.error('No tracker found in GA.');
        }
      });
    }
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);
  useEffect(() => {
    const errors = form.formState.errors;
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0 && form.formState.isSubmitted) {
      // Define the field order as they appear visually on the form
      const fieldOrder = ['firstName', 'lastName', 'dateOfBirth', 'email', 'phone', 'address', 'city', 'zipCode', 'state'];

      // Find the first error field based on visual order, not object key order
      const firstErrorField = fieldOrder.find(field => errors[field]) || errorKeys[0];
      setTimeout(() => {
        let el: Element | null = null;
        if (firstErrorField === 'dateOfBirth') {
          el = document.querySelector('[name="dateOfBirth"]') || document.querySelector('input[autocomplete="bday"]') || document.querySelector('input[type="date"]');
        } else {
          el = document.querySelector(`[name="${firstErrorField}"]`);
        }

        // If it's wrapped in MUI components, try to find the input inside
        if (el && el.tagName !== 'INPUT') {
          const inputInside = el.querySelector('input');
          if (inputInside) {
            el = inputInside;
          }
        }
        if (el instanceof HTMLElement) {
          if (document.activeElement !== el) {
            el.focus();
            const rect = el.getBoundingClientRect();
            const offsetTop = window.scrollY + rect.top - 100;
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });
          }
        }
      }, 100);
    }
  }, [form.formState.errors, form.formState.isSubmitted]);
  const handleSubmit: SubmitHandler<FieldValues> = async (fields: FieldValues) => {
    if (isStepSubmitting) {
      return;
    }
    const unacceptableFields = unacceptableInputs([{
      name: 'firstName',
      value: fields.firstName
    }, {
      name: 'lastName',
      value: fields.lastName
    }, {
      name: 'address',
      value: fields.address
    }, {
      name: 'city',
      value: fields.city
    }]);
    setIsStepSubmitting(true);
    if (unacceptableFields.length) {
      console.error('offensive inputs detected: ', unacceptableFields.join(', '));
      setOffensiveInputsFound(true);
      setIsStepSubmitting(false);
      return;
    } else {
      setOffensiveInputsFound(false);
    }
    const formattedDob = format(new Date(fields.dateOfBirth), 'MM/dd/yyyy');
    const answerObject = createAnswerObject(questionData, questionsAnswers);
    const requestData = {
      subject: 'leads',
      answers: {
        first_name: fields.firstName.trim(),
        last_name: fields.lastName.trim(),
        email: fields.email.trim(),
        phone: fields.phone,
        dob: formattedDob,
        street: fields.address.trim(),
        zip_code: fields.zipCode,
        city: fields.city.trim(),
        state: fields.state,
        ...answerObject
      },
      formId: leadFormId
    };
    if (leadId) {
      const appointmentCheckUrl = `${process.env.APP_PATH}/api/clear-choice/is-existing-lead/`;
      try {
        const response = await fetch(appointmentCheckUrl, {
          headers: makeRequestHeaders({
            sessionId,
            enableHydraCalls
          }),
          method: 'POST',
          body: JSON.stringify({
            leadId: leadId
          })
        });
        const data = await response.json();
        if (data.appointments && data.appointments.length > 0) {
          setSchedulePopupError(true);
          setModalType('existingConsultation');
          setIsStepSubmitting(false);
          return;
        }
        if (response.status !== 404) {
          setSchedulePopupError(true);
          setModalType('promptToCall');
          setIsStepSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    }
    try {
      const response = await fetch(process.env.APP_PATH + '/api/clear-choice/submit-patient-questions/', {
        headers: makeRequestHeaders({
          sessionId,
          enableHydraCalls
        }),
        method: 'POST',
        body: JSON.stringify(requestData)
      });
      const result = await response.json();
      result?.failure_list?.forEach((errorCode: string) => {
        const error = errorMapping[errorCode];
        if (error) {
          form.setError(error.field, {
            message: t(error.message)
          });
        }
      });
      result.ptc_step_3 === 1 && setStepOnePtc(true);
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const salesforceGuid = result.lead?.sfid;
      const salesforceAccountId = result.lead?.sfaccountid;
      if (salesforceGuid) {
        setSalesforceGuid(salesforceGuid);
      }
      if (salesforceAccountId) {
        setSalesforceAccountId(salesforceAccountId);
      }
      setLeadId(result.lead.id);
      let gaDataPayload;
      if (result?.lead?.id && result?.lead?.sfid) {
        gaDataPayload = {
          ga_client_id: GAClientId,
          ga_track_id: GATrackingId,
          gclid: gclid || localStorage.getItem('gclid'),
          userId: result.lead.sfid,
          leadId: result.lead.id
        };
      }

      // Submit GA data
      if (result?.lead?.id && result?.lead?.sfid && gaDataPayload) {
        await fetch(process.env.APP_PATH + '/api/clear-choice/save-ga-data/', {
          headers: makeRequestHeaders({
            sessionId,
            enableHydraCalls
          }),
          method: 'PATCH',
          body: JSON.stringify(gaDataPayload)
        });
      }

      // submit marketing data
      if (result?.lead?.id) {
        const parsedCookies = new Cookies();
        const marketingSource: string | undefined = parsedCookies.get('marketing_source');
        if (marketingSource) {
          parseAndSaveMarketingData(marketingSource, {
            leadId: result?.lead?.id as string,
            session_id: result?.session?.id as string,
            domain: process.env.NEXT_PUBLIC_SITE_URL,
            pathname: router.basePath,
            referrer: referrer ?? ''
          }, enableHydraCalls, sessionId, transformMarketingSourceEnabled, logger);
        }
      }

      // Capture VWO experiment data
      const experimentData = getExperimentData();
      const payload = {
        experimentData: experimentData,
        leadId: result?.lead?.id
      };
      const submitExperimentData = async () => {
        try {
          const response = await fetch(process.env.APP_PATH + '/api/clear-choice/save-experiment-data/', {
            headers: makeRequestHeaders({
              sessionId,
              enableHydraCalls
            }),
            method: 'PUT',
            body: JSON.stringify(payload)
          });
          if (!response.ok) {
            throw new Error(`Failed to submit experiment data: ${response.statusText}`);
          }
        } catch (error) {
          console.error('Error submitting experiment data:', error);
        }
      };
      if (result?.lead?.id && Array.isArray(payload.experimentData) && payload.experimentData.length > 0 && payload.leadId) {
        if (typeof window !== 'undefined' && window.LogRocket) {
          experimentData.forEach(experiment => {
            window.LogRocket.track('VWO-ClearChoice', {
              referrerPage: experiment.referrerPage ?? '',
              experimentVariation: experiment.experimentVariation ?? '',
              experimentID: experiment.experimentID ?? '',
              conversionPage: experiment.conversionPage ?? '',
              timestampFirstTouch: experiment.timestampFirstTouch ?? ''
            });
          });
        }
        submitExperimentData();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsStepSubmitting(false);
      return;
    }
    setIsStepSubmitting(false);
    incrementStep();
  };
  return <>
			<Form form={form} onSubmit={handleSubmit} formId="step-2-form" data-test-id="step-2-form" data-sentry-element="Form" data-sentry-source-file="PatientInformation.tsx">
				<Grid mt={2} data-sentry-element="Grid" data-sentry-source-file="PatientInformation.tsx">
					<PersonalInfo data-sentry-element="PersonalInfo" data-sentry-source-file="PatientInformation.tsx" />
					<Address form={form} smartyKey={smartyKey} data-sentry-element="Address" data-sentry-source-file="PatientInformation.tsx" />
				</Grid>
				{offensiveInputsFound && <Grid mt={2} item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('inappropriateText')}</Typography>
						</Alert>
					</Grid>}
			</Form>
		</>;
}